import { lazy, Suspense } from "react";
import { Navigate, Outlet, useRoutes } from "react-router-dom";
import ProtectedRoute from "../../components/ProtectedRoute";
import ProviderRoute from "../../components/ProviderRoute";
import RedirectIfAuthenticated from "../../components/RedirectIfAuthenticated";
import PageLoader from "../../components/PageLoader";

// Auth pages (will be conditionally styled)
const LoginPage = lazy(() => import("../../features/auth/pages/Login"));
const SignUpPage = lazy(() => import("../../features/auth/pages/SignUp"));
const ForgotPasswordPage = lazy(
  () => import("../../features/auth/pages/ForgotPassword"),
);
const OAuthCallbackPage = lazy(
  () => import("../../features/auth/pages/OAuthCallback"),
);

// Provider pages
const ProviderIndexPage = lazy(
  () => import("../../features/provider/pages/ProviderIndexPage"),
);
const ProviderMarketingPage = lazy(
  () => import("../../features/provider/pages/ProviderMarketingPage"),
);
const UpgradeToProviderPage = lazy(
  () => import("../../features/provider/pages/UpgradeToProviderPage"),
);
const ProviderDashboard = lazy(
  () => import("../../features/provider/pages/ProviderDashboard"),
);
const ProviderLayout = lazy(
  () => import("../../features/provider/components/ProviderLayout"),
);

// Jobs
const JobsPage = lazy(() => import("../../features/provider/pages/JobsPage"));
const JobDetailPage = lazy(
  () => import("../../features/provider/pages/JobDetailPage"),
);

// Requests
const RequestsPage = lazy(
  () => import("../../features/provider/pages/RequestsPage"),
);
const PrivateRequestDetailPage = lazy(
  () => import("../../features/provider/pages/PrivateRequestDetailPage"),
);
const PublicRequestDetailPage = lazy(
  () => import("../../features/provider/pages/PublicRequestDetailPage"),
);

// Settings
const ProviderSettingsLayout = lazy(
  () => import("../../features/provider/pages/settings/ProviderSettingsLayout"),
);
const ProviderSettingsIndex = lazy(
  () => import("../../features/provider/pages/settings/ProviderSettingsIndex"),
);
const ProviderProfilePage = lazy(
  () => import("../../features/provider/pages/settings/ProviderProfilePage"),
);
const CategoriesPage = lazy(
  () => import("../../features/provider/pages/settings/CategoriesPage"),
);
const PaymentSetupPage = lazy(
  () => import("../../features/provider/pages/settings/PaymentSetupPage"),
);
const SecurityPage = lazy(
  () => import("../../features/provider/pages/settings/SecurityPage"),
);

// Stripe account setup
const OnboardingPage = lazy(
  () => import("../../features/provider/pages/OnboardingPage"),
);
const OnboardingReturnPage = lazy(
  () => import("../../features/provider/pages/OnboardingReturnPage"),
);

const NotFoundPage = lazy(() => import("../../features/error/NotFoundPage"));
const TermsOfServicePage = lazy(
  () => import("../../features/legal/pages/TermsOfServicePage"),
);
const PrivacyPolicyPage = lazy(
  () => import("../../features/legal/pages/PrivacyPolicyPage"),
);
const ProviderPublicLayout = lazy(
  () => import("../../features/provider/components/ProviderPublicLayout"),
);
const AboutPage = lazy(() => import("../../features/about/pages/AboutPage"));
const CareersPage = lazy(
  () => import("../../features/careers/pages/CareersPage"),
);

const ProviderApp = () => {
  const appRoutes = [
    // Auth routes
    {
      path: "auth/login",
      element: (
        <RedirectIfAuthenticated redirectTo="/">
          <LoginPage />
        </RedirectIfAuthenticated>
      ),
    },
    {
      path: "auth/signup",
      element: (
        <RedirectIfAuthenticated redirectTo="/">
          <SignUpPage />
        </RedirectIfAuthenticated>
      ),
    },
    {
      path: "auth/forgot-password",
      element: (
        <RedirectIfAuthenticated redirectTo="/">
          <ForgotPasswordPage />
        </RedirectIfAuthenticated>
      ),
    },

    // OAuth callback route
    {
      path: "oauth/callback",
      element: <OAuthCallbackPage />,
    },

    // Public routes - shared layout with nav + footer
    {
      element: <ProviderPublicLayout />,
      children: [
        {
          index: true,
          element: <ProviderIndexPage />,
        },
        {
          path: "marketing",
          element: <ProviderMarketingPage />,
        },
        {
          path: "about",
          element: <AboutPage />,
        },
        {
          path: "careers",
          element: <CareersPage />,
        },
        {
          path: "terms",
          element: <TermsOfServicePage />,
        },
        {
          path: "privacy",
          element: <PrivacyPolicyPage />,
        },
      ],
    },

    // Public request handler (no auth required - from SMS/email)
    {
      path: "r/:token",
      element: <PublicRequestDetailPage />,
    },

    // Protected provider routes
    {
      element: (
        <ProviderRoute>
          <ProviderLayout>
            <Outlet />
          </ProviderLayout>
        </ProviderRoute>
      ),
      children: [
        {
          path: "dashboard",
          element: <ProviderDashboard />,
        },
        {
          path: "jobs",
          element: <JobsPage />,
        },
        {
          path: "jobs/:jobId",
          element: <JobDetailPage />,
        },
        {
          path: "requests",
          element: <RequestsPage />,
        },
        {
          path: "requests/:bookingLinkId",
          element: <PrivateRequestDetailPage />,
        },
        {
          path: "onboarding",
          element: <OnboardingPage />,
        },
        {
          path: "onboarding/return",
          element: <OnboardingReturnPage />,
        },
      ],
    },

    // Settings routes (protected but separate layout)
    {
      path: "settings",
      element: (
        <ProviderRoute>
          <ProviderSettingsLayout />
        </ProviderRoute>
      ),
      children: [
        {
          index: true,
          element: <ProviderSettingsIndex />,
        },
        {
          path: "profile",
          element: <ProviderProfilePage />,
        },
        {
          path: "categories",
          element: <CategoriesPage />,
        },
        {
          path: "payment",
          element: <PaymentSetupPage />,
        },
        {
          path: "security",
          element: <SecurityPage />,
        },
      ],
    },

    // Upgrade route (protected but not provider-only)
    {
      path: "upgrade",
      element: (
        <ProtectedRoute>
          <UpgradeToProviderPage />
        </ProtectedRoute>
      ),
    },

    // 404
    {
      path: "404",
      element: <NotFoundPage />,
    },
    {
      path: "*",
      element: <Navigate to="/404" replace />,
    },
  ];

  return useRoutes(appRoutes);
};

export default function ProviderAppRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <ProviderApp />
    </Suspense>
  );
}
